import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Divider, Link, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';

import useBackendApi from '../hooks/useBackendApi';
import GradientCircularProgress from '../components/common/GradientCircularProgress';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';
import {
  ExtractAnswersResponse,
  MatrixDurationAnswer,
  MatrixYesNoAnswer,
  QuestionAnswer,
} from '../types/questionnaire';
import { colors } from '../styles/fontStyles';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type RouteParams = {
  org_path_name: string;
  acc_path_name: string;
  patient_user_id: string;
  submission_id: string;
};

type MatrixBlock = {
  kind: 'matrix';
  key: string;
  title: string;
  rows: Array<{ label: string; value: string }>;
};

type DurationBlock = {
  kind: 'duration';
  key: string;
  title: string;
  rows: Array<{ label: string; hours: number | null; mins: number | null }>;
};

type OtherField = {
  kind: 'other';
  key: string;
  value: Exclude<QuestionAnswer, MatrixYesNoAnswer | MatrixDurationAnswer>;
  title: string;
};

type QuestionnaireBlock = MatrixBlock | DurationBlock | OtherField;

type HeightMeasurement = {
  feetInches: string;
  cm: string;
};

type WeightMeasurement = {
  lbs: string;
  kgs: string;
};

type HighlightValue = string | number | boolean | null;

const textWrapSx = {
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
} as const;

const NEGATIVE_HIGHLIGHT_QUESTION_KEYS = new Set([
  'asthmaControl',
  'asthmaPhysician',
  'doctor_chest_lung',
  'see_dr_health_conditions',
  'doctor_other_lung',
  'doctor_lung_conditions',
  'doctor_other_lung_symptoms',
  'doctor_cardiac_problems',
  'doctor_cardiac_symptoms',
  'control_chest_lung',
  'control_health_conditions',
  'control_other_lung',
  'control_lung_conditions',
  'control_cardiac_problems',
]);

const FOLLOW_UP_HIGHLIGHT_TRIGGER_BY_TARGET_KEY = new Map([
  ['describe_other_lung_problem', 'any_other_lung_problems'],
  ['details_other_lung_symptoms', 'other_lung_symptoms'],
]);

const HIDDEN_QUESTION_KEYS = new Set([
  'calculate_referral',
  'calculate_chest_lung',
  'calculate_conditions',
  'calculate_lung_conditions',
  'calculate_medication',
  'calculate_cardiac_symptom',
  'calculate_cardiac_problem',
  'cough_blood',
  'medical',
  'exposures',
  'asthmaCheck',
  'referralPossible',
  'hazardousexposurewidget',
  'typeA104',
]);

function isObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function isUrlString(v: unknown): v is string {
  return typeof v === 'string' && /^https?:\/\//i.test(v.trim());
}

function cleanLabel(s: string): string {
  return s
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
}

function formatNestedLabel(s: string): string {
  const cleaned = cleanLabel(s).replace(/_/g, ' ');
  return cleaned ? `${cleaned.charAt(0).toUpperCase()}${cleaned.slice(1)}` : cleaned;
}

function decodeHtmlEntities(s: string): string {
  return s
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>');
}

function isAffirmativeValue(v: HighlightValue): boolean {
  if (typeof v !== 'string') return false;
  return cleanLabel(v).toLowerCase() === 'yes';
}

function isNegativeValue(v: HighlightValue): boolean {
  if (typeof v !== 'string') return false;
  return cleanLabel(v).toLowerCase() === 'no';
}

function isHighlightValue(v: QuestionAnswer | undefined): v is HighlightValue {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' || v === null;
}

function highlightsNegativeAnswer(k: string): boolean {
  return NEGATIVE_HIGHLIGHT_QUESTION_KEYS.has(k);
}

function shouldHighlightAnswer(k: string, v: HighlightValue): boolean {
  return highlightsNegativeAnswer(k) ? isNegativeValue(v) : isAffirmativeValue(v);
}

function getAnswerHighlightTextSx(k: string, v: HighlightValue) {
  return shouldHighlightAnswer(k, v) ? { color: colors.redDark } : {};
}

function shouldHighlightFollowUpSection(questionKey: string, answersByKey: Record<string, QuestionAnswer>): boolean {
  const triggerKey = FOLLOW_UP_HIGHLIGHT_TRIGGER_BY_TARGET_KEY.get(questionKey);
  const triggerValue = triggerKey ? answersByKey[triggerKey] : undefined;
  return !!triggerKey && isHighlightValue(triggerValue) ? isAffirmativeValue(triggerValue) : false;
}

function getAnswerBlockSx(questionKey: string, themeBorderColor: string, answersByKey: Record<string, QuestionAnswer>) {
  const shouldHighlightFollowUp = shouldHighlightFollowUpSection(questionKey, answersByKey);

  return {
    p: 2,
    borderRadius: '12px',
    border: '1px solid',
    borderColor: shouldHighlightFollowUp ? colors.redDark : themeBorderColor,
    backgroundColor: shouldHighlightFollowUp ? colors.lightBeige : 'transparent',
  };
}

function isYesNoMatrix(v: QuestionAnswer): v is MatrixYesNoAnswer {
  return isObject(v) && Array.isArray(v.rows) && 'value' in (v.rows[0] as MatrixYesNoAnswer['rows'][number]);
}

function isDurationMatrix(v: QuestionAnswer): v is MatrixDurationAnswer {
  return (
    isObject(v) &&
    Array.isArray(v.rows) &&
    ('hours' in (v.rows[0] as MatrixDurationAnswer['rows'][number]) ||
      'mins' in (v.rows[0] as MatrixDurationAnswer['rows'][number]))
  );
}

function getMeasurementWidgetValues(raw: string): Record<string, string> {
  const obj = JSON.parse(raw)[0] as Record<string, string>;
  return Object.keys(obj).reduce<Record<string, string>>((acc, k) => {
    const value = obj[k];
    const normalizedKey = decodeHtmlEntities(k).trim().toLowerCase();
    acc[normalizedKey] = String(value).trim();
    return acc;
  }, {});
}

function getHeightMeasurement(raw: string): HeightMeasurement {
  const keys = getMeasurementWidgetValues(raw);
  return {
    feetInches: keys['feet & inches'],
    cm: keys['cm'],
  };
}

function getWeightMeasurement(raw: string): WeightMeasurement {
  const keys = getMeasurementWidgetValues(raw);
  return {
    lbs: keys['lbs'],
    kgs: keys['kgs'],
  };
}

function calculateBmi(height: HeightMeasurement, weight: WeightMeasurement): string {
  const heightCm = Number(height.cm);
  const weightKg = Number(weight.kgs);
  const heightMeters = heightCm / 100;
  const bmi = weightKg / (heightMeters * heightMeters);

  return bmi.toFixed(1);
}

function coerceSpecialKeyValue(
  k: string,
  v: OtherField['value'],
): Exclude<QuestionAnswer, MatrixYesNoAnswer | MatrixDurationAnswer> | string {
  if (k === 'height') {
    const height = getHeightMeasurement(v as string);
    return `${height.feetInches} (${height.cm} cm)`;
  }

  if (k === 'weight') {
    const weight = getWeightMeasurement(v as string);
    return `${weight.lbs} lbs (${weight.kgs} kg)`;
  }

  return v;
}

function hasHighlightedAnswerValue(k: string, v: OtherField['value']): boolean {
  const vv = coerceSpecialKeyValue(k, v);

  if (typeof vv === 'string' || typeof vv === 'number' || typeof vv === 'boolean') {
    return shouldHighlightAnswer(k, cleanLabel(String(vv)));
  }

  if (Array.isArray(vv)) {
    return vv.some((item) => shouldHighlightAnswer(k, cleanLabel(String(item))));
  }

  if (isObject(vv)) {
    return Object.values(vv).some((item) => shouldHighlightAnswer(k, cleanLabel(String(item))));
  }

  return false;
}

function isHighlightedBlock(block: QuestionnaireBlock, answersByKey: Record<string, QuestionAnswer>): boolean {
  if (shouldHighlightFollowUpSection(block.key, answersByKey)) {
    return true;
  }

  if (block.kind === 'matrix') {
    return block.rows.some((row) => shouldHighlightAnswer(block.key, row.value));
  }

  if (block.kind === 'other') {
    return hasHighlightedAnswerValue(block.key, block.value);
  }

  return false;
}

function KeyValueRow({
  k,
  v,
  labelOverride,
}: {
  k: string;
  v: OtherField['value'];
  labelOverride?: string;
}): JSX.Element {
  const label = cleanLabel(labelOverride ?? k);
  const vv = coerceSpecialKeyValue(k, v);

  const questionSx = {
    fontSize: '16px',
    fontWeight: 700,
    color: colors.primaryBlue,
    ...textWrapSx,
  } as const;

  const answerSx = {
    minWidth: 0,
    fontSize: '16px',
    lineHeight: 1.6,
    ...textWrapSx,
  } as const;

  const nestedLabelSx = {
    fontSize: '13px',
    fontWeight: 700,
    opacity: 0.7,
    ...textWrapSx,
  } as const;

  const rowSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.25,
  } as const;

  if (vv === null || vv === undefined || vv === '') {
    return (
      <Box sx={rowSx}>
        <Typography sx={questionSx}>{label}</Typography>
        <Typography sx={{ opacity: 0.75, ...answerSx }}>—</Typography>
      </Box>
    );
  }

  if (isUrlString(vv)) {
    return (
      <Box sx={rowSx}>
        <Typography sx={questionSx}>{label}</Typography>
        <Box sx={{ ...answerSx }}>
          <Link href={vv} target="_blank" rel="noreferrer">
            Open link
          </Link>
        </Box>
      </Box>
    );
  }

  if (typeof vv === 'string' || typeof vv === 'number' || typeof vv === 'boolean') {
    const displayValue = cleanLabel(String(vv));
    return (
      <Box sx={rowSx}>
        <Typography sx={questionSx}>{label}</Typography>
        <Typography sx={{ ...answerSx, ...getAnswerHighlightTextSx(k, displayValue) }}>{displayValue}</Typography>
      </Box>
    );
  }

  if (Array.isArray(vv)) {
    const items = vv.map((x) => ({ display: cleanLabel(String(x)) })).filter((x) => x.display);
    return (
      <Box sx={rowSx}>
        <Typography sx={questionSx}>{label}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, ...answerSx }}>
          {items.length ? (
            items.map((it, idx) => (
              <Typography key={`${k}-${idx}`} sx={{ ...answerSx, ...getAnswerHighlightTextSx(k, it.display) }}>
                • {it.display}
              </Typography>
            ))
          ) : (
            <Typography sx={{ opacity: 0.75, ...answerSx }}>—</Typography>
          )}
        </Box>
      </Box>
    );
  }

  if (isObject(vv)) {
    const entries = Object.entries(vv);
    const isFullNameAnswer =
      entries.length === 2 &&
      entries.every(([kk]) => {
        const normalizedKey = cleanLabel(kk).toLowerCase();
        return normalizedKey === 'first' || normalizedKey === 'last';
      });

    return (
      <Box sx={rowSx}>
        <Typography sx={questionSx}>{label}</Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1.25,
            gridTemplateColumns: isFullNameAnswer ? { xs: '1fr', sm: '1fr 1fr' } : '1fr',
            ...answerSx,
          }}
        >
          {entries.map(([kk, vvv]) => {
            const displayValue = vvv === null || vvv === undefined ? '—' : cleanLabel(String(vvv));
            return (
              <Box key={`${k}-${kk}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography sx={nestedLabelSx}>{formatNestedLabel(kk)}</Typography>
                <Typography sx={{ ...answerSx, ...getAnswerHighlightTextSx(k, displayValue) }}>
                  {displayValue}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  const displayValue = cleanLabel(String(vv));
  return (
    <Box sx={rowSx}>
      <Typography sx={questionSx}>{label}</Typography>
      <Typography sx={{ ...answerSx, ...getAnswerHighlightTextSx(k, displayValue) }}>{displayValue}</Typography>
    </Box>
  );
}

function MatrixCard({
  questionKey,
  title,
  rows,
  themeBorderColor,
}: {
  questionKey: string;
  title: string;
  rows: Array<{ label: string; value: string }>;
  themeBorderColor: string;
}): JSX.Element {
  const gridTemplateColumns = '1fr 110px';
  const gridLine = `1px solid ${themeBorderColor}`;

  return (
    <GridAnswerCard
      title={title}
      themeBorderColor={themeBorderColor}
      gridTemplateColumns={gridTemplateColumns}
      leftHeader="Question"
      rightHeader="Answer"
    >
      {rows.map((r, idx) => (
        <Box
          key={`${idx}-${r.label}`}
          sx={{ display: 'grid', gridTemplateColumns, borderTop: idx === 0 ? 'none' : gridLine }}
        >
          <Box sx={{ px: 1.5, py: 1, borderRight: gridLine }}>
            <Typography sx={{ fontSize: '14px' }}>{cleanLabel(r.label)}</Typography>
          </Box>
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 700,
                textAlign: 'right',
                ...getAnswerHighlightTextSx(questionKey, r.value),
              }}
            >
              {cleanLabel(r.value || '—')}
            </Typography>
          </Box>
        </Box>
      ))}
    </GridAnswerCard>
  );
}

function DurationMatrixCard({
  title,
  rows,
  themeBorderColor,
}: {
  title: string;
  rows: Array<{ label: string; hours: number | null; mins: number | null }>;
  themeBorderColor: string;
}): JSX.Element {
  const fmt = (h: number | null, m: number | null) => {
    const hh = h ?? 0;
    const mm = m ?? 0;
    if (!hh && !mm) return '—';
    const parts: string[] = [];
    if (hh) parts.push(`${hh}h`);
    if (mm) parts.push(`${mm}m`);
    return parts.join(' ');
  };

  const gridTemplateColumns = '1fr 140px';
  const gridLine = `1px solid ${themeBorderColor}`;

  return (
    <GridAnswerCard
      title={title}
      themeBorderColor={themeBorderColor}
      gridTemplateColumns={gridTemplateColumns}
      leftHeader="Work effort"
      rightHeader="Duration"
    >
      {rows.map((r, idx) => (
        <Box
          key={`${idx}-${r.label}`}
          sx={{ display: 'grid', gridTemplateColumns, borderTop: idx === 0 ? 'none' : gridLine }}
        >
          <Box sx={{ px: 1.5, py: 1, borderRight: gridLine }}>
            <Typography sx={{ fontSize: '14px' }}>{cleanLabel(r.label)}</Typography>
          </Box>
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 700, textAlign: 'right' }}>
              {fmt(r.hours, r.mins)}
            </Typography>
          </Box>
        </Box>
      ))}
    </GridAnswerCard>
  );
}

function GridAnswerCard({
  title,
  themeBorderColor,
  gridTemplateColumns,
  leftHeader,
  rightHeader,
  children,
}: {
  title: string;
  themeBorderColor: string;
  gridTemplateColumns: string;
  leftHeader: string;
  rightHeader: string;
  children: React.ReactNode;
}): JSX.Element {
  const gridLine = `1px solid ${themeBorderColor}`;

  return (
    <Box sx={{ p: 2, borderRadius: '12px', border: '1px solid', borderColor: themeBorderColor }}>
      <Typography sx={{ fontWeight: 800, mb: 1 }}>{title}</Typography>

      <Box sx={{ border: gridLine, borderRadius: '10px', overflow: 'hidden' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns }}>
          <Box sx={{ px: 1.5, py: 1, borderRight: gridLine }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 800, opacity: 0.9 }}>{leftHeader}</Typography>
          </Box>
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 800, opacity: 0.9, textAlign: 'right' }}>
              {rightHeader}
            </Typography>
          </Box>
        </Box>

        <Divider />

        {children}
      </Box>
    </Box>
  );
}

function AnswerBlockContainer({
  blockId,
  reviewButton,
  children,
}: {
  blockId: string;
  reviewButton?: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box id={blockId} sx={{ scrollMarginTop: '96px' }}>
      {reviewButton}
      {children}
    </Box>
  );
}

export default function QuestionnaireAnswers(): JSX.Element {
  const { apiFetchRequest } = useBackendApi();
  const { theme } = useThemeContext();

  const { org_path_name, acc_path_name, patient_user_id, submission_id } = useParams<RouteParams>();

  const org = useMemo(() => (org_path_name || '').trim(), [org_path_name]);
  const acc = useMemo(() => (acc_path_name || '').trim(), [acc_path_name]);
  const patient = useMemo(() => (patient_user_id || '').trim(), [patient_user_id]);
  const submissionId = useMemo(() => (submission_id || '').trim(), [submission_id]);

  const [data, setData] = useState<ExtractAnswersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [activeHighlightedKey, setActiveHighlightedKey] = useState<string | null>(null);
  const [nextHighlightedIndex, setNextHighlightedIndex] = useState(0);

  const fail = () => setErrorText('Unable to load questionnaire answers. Please try again.');

  const fetchAnswers = useCallback(async () => {
    if (!org || !acc || !patient || !submissionId) return;

    setErrorText(null);
    setLoading(true);

    try {
      const endpoint = `${backendURL}/o/${encodeURIComponent(org)}/a/${encodeURIComponent(
        acc,
      )}/patients/${encodeURIComponent(patient)}/questionnaires/${encodeURIComponent(submissionId)}`;

      const res = await apiFetchRequest({ endpoint, method: 'GET' });
      if (!res) return fail();

      const { status, body, isError } = res;

      const unauthorized =
        status === 401 ||
        body?.status_code === 401 ||
        body?.message === 'UNAUTHENTICATED' ||
        body?.message === 'UNAUTHORIZED' ||
        body?.message === 'NO_ACCESS';

      if (unauthorized) {
        window.location.href = '/401.html';
        return;
      }

      if (isError || status !== 200) {
        const msg = body?.detail || body?.message || body?.error || 'Unable to load questionnaire answers.';
        setErrorText(String(msg));
        return;
      }

      setData(body as ExtractAnswersResponse);
    } catch {
      fail();
    } finally {
      setLoading(false);
    }
  }, [acc, apiFetchRequest, org, patient, submissionId]);

  useEffect(() => {
    fetchAnswers();
  }, [fetchAnswers]);

  useEffect(() => {
    setActiveHighlightedKey(null);
    setNextHighlightedIndex(0);
  }, [data?.submission_id]);

  const themeBorderColor = theme.custom.pages.frost.authHome.cards.borderColor;

  const answerBlocks = useMemo(() => {
    const answers = data?.answers ?? {};
    const qm = data?.question_meta ?? {};
    const height = typeof answers.height === 'string' ? getHeightMeasurement(answers.height) : null;
    const weight = typeof answers.weight === 'string' ? getWeightMeasurement(answers.weight) : null;
    const bmiValue = height && weight ? calculateBmi(height, weight) : null;

    const sortByOrder = (a: string, b: string) => {
      const orderA = qm[a].order;
      const orderB = qm[b].order;

      if (orderA !== orderB) return orderA - orderB;
      return a.localeCompare(b);
    };

    const entries = Object.entries(answers)
      .filter(([key]) => qm[key] && !HIDDEN_QUESTION_KEYS.has(key))
      .sort(([ka], [kb]) => sortByOrder(ka, kb));

    const blocks: QuestionnaireBlock[] = [];

    for (const [k, v] of entries) {
      const metaTitle = cleanLabel(qm[k]?.title ?? k);

      if (isYesNoMatrix(v)) {
        const title = cleanLabel(v.question_title);
        const rows = v.rows.map((r) => ({
          label: cleanLabel(r.label),
          value: r.value,
        }));
        blocks.push({ kind: 'matrix', key: k, title, rows });
        continue;
      }

      if (isDurationMatrix(v)) {
        const title = cleanLabel(v.question_title);
        const rows = v.rows.map((r) => ({
          label: cleanLabel(r.label),
          hours: r.hours,
          mins: r.mins,
        }));
        blocks.push({ kind: 'duration', key: k, title, rows });
        continue;
      }

      blocks.push({ kind: 'other', key: k, value: v, title: metaTitle });

      if (k === 'height' && bmiValue) {
        blocks.push({ kind: 'other', key: 'bmi', value: bmiValue, title: 'Body Mass Index (BMI)' });
      }
    }

    return blocks;
  }, [data]);

  const answersByKey = data?.answers ?? {};
  const highlightedBlockKeys = useMemo(
    () => answerBlocks.filter((block) => isHighlightedBlock(block, answersByKey)).map((block) => block.key),
    [answerBlocks, answersByKey],
  );

  const goToNextHighlighted = useCallback(() => {
    const targetKey = highlightedBlockKeys[nextHighlightedIndex % highlightedBlockKeys.length];
    const element = document.getElementById(`questionnaire-block-${targetKey}`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveHighlightedKey(targetKey);
    setNextHighlightedIndex((nextHighlightedIndex + 1) % highlightedBlockKeys.length);
  }, [highlightedBlockKeys, nextHighlightedIndex]);

  const renderReviewButton = (label: string) => (
    <Button variant="outlined" onClick={goToNextHighlighted}>
      {label}
    </Button>
  );

  const renderAnswerBlock = (block: QuestionnaireBlock) => {
    const blockId = `questionnaire-block-${block.key}`;
    const blockNextButton =
      activeHighlightedKey === block.key ? (
        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'flex-end' }}>{renderReviewButton('Next')}</Box>
      ) : null;

    if (block.kind === 'matrix') {
      return (
        <AnswerBlockContainer key={block.key} blockId={blockId} reviewButton={blockNextButton}>
          <MatrixCard
            questionKey={block.key}
            title={block.title}
            rows={block.rows}
            themeBorderColor={themeBorderColor}
          />
        </AnswerBlockContainer>
      );
    }

    if (block.kind === 'duration') {
      return (
        <AnswerBlockContainer key={block.key} blockId={blockId} reviewButton={blockNextButton}>
          <DurationMatrixCard title={block.title} rows={block.rows} themeBorderColor={themeBorderColor} />
        </AnswerBlockContainer>
      );
    }

    return (
      <AnswerBlockContainer key={block.key} blockId={blockId} reviewButton={blockNextButton}>
        <Box sx={getAnswerBlockSx(block.key, themeBorderColor, answersByKey)}>
          <KeyValueRow k={block.key} v={block.value} labelOverride={block.title} />
        </Box>
      </AnswerBlockContainer>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.layout.defaults.backgroundColor,
        borderRadius: '16px',
        padding: { sm: '28px', xs: '20px' },
      }}
    >
      <Typography variant="h6" sx={{ mb: 1 }}>
        Questionnaire Answers
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <GradientCircularProgress />
        </Box>
      ) : null}

      {errorText ? (
        <Box sx={{ py: 2 }}>
          <Typography color="error">{errorText}</Typography>
        </Box>
      ) : null}

      {!loading && !errorText ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Typography>{data?.form_title ?? ''}</Typography>
            {highlightedBlockKeys.length && !activeHighlightedKey ? renderReviewButton('Start Review') : null}
          </Box>
          {answerBlocks.map(renderAnswerBlock)}

          {!answerBlocks.length ? <Typography>No answers found.</Typography> : null}

          {data?.error ? (
            <Typography sx={{ mt: 1, fontSize: '13px', opacity: 0.8 }} color="error">
              {String(data.error)}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
